import type {
  ActivityTripRide,
  ResultsVehicleRow,
  SimulationActivity,
  UnmatchedRide,
} from '../../types/simulation';
import type { RoadRouteWithMetadata } from './resultsPathsService';

export type SeatType = 'standard' | 'wheelchair';
export type SeatCounts = Readonly<Record<SeatType, number>>;
export type KpiDirection = 'higher_is_better' | 'lower_is_better' | 'neutral';
export type KpiTone = 'good' | 'bad' | 'neutral';

export type LatLngPair = readonly [number, number];

export interface RideTiming {
  readonly rideId: string;
  readonly externalReferenceId: string | null;
  readonly shiftId: string;
  readonly requestType: string | null;
  readonly requestMs: number | null;
  readonly pickMs: number;
  readonly dropMs: number;
  readonly pax: SeatCounts;
  readonly from: LatLngPair | null;
  readonly to: LatLngPair | null;
}

export interface OccupancySegment {
  readonly startMs: number;
  readonly endMs: number;
  readonly aboard: SeatCounts;
}

export interface StopEvent {
  readonly atMs: number;
  readonly kind: 'pick' | 'drop';
  readonly rideId: string;
}

export interface ShiftSummary {
  readonly shiftId: string;
  readonly vehicleId: string | null;
  /** 1-based, ordered by shift start. */
  readonly ordinal: number;
  readonly startMs: number;
  readonly endMs: number;
  readonly seats: SeatCounts;
  readonly trips: number;
  readonly pax: number;
  /** Most passengers aboard at once ÷ seats; null when the vehicle has no seats of that type. */
  readonly peakLoadFactor: Readonly<Record<SeatType, number | null>>;
  readonly segments: readonly OccupancySegment[];
  readonly stops: readonly StopEvent[];
}

export interface RequestBucket {
  readonly startMs: number;
  readonly matched: number;
  readonly unmatched: number;
}

export interface ServiceDay {
  readonly startMs: number;
  readonly endMs: number;
}

export interface HourlyPeakLoad {
  readonly startMs: number;
  readonly standard: number | null;
  readonly wheelchair: number | null;
}

interface PartialRide {
  shiftId: string;
  ride: ActivityTripRide;
  pickMs?: number;
  dropMs?: number;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const OFFSET_SUFFIX = /(z|[+-]\d\d:?\d\d)$/i;
const EARLIEST_VALID_MS = Date.UTC(2000, 0, 1);

/** Results timestamps without an offset are UTC; anything before 2000 is a placeholder, not a time. */
export function parseResultTime(value: string | null | undefined): number | null {
  const text = value?.trim();
  if (!text) return null;
  const ms = Date.parse(OFFSET_SUFFIX.test(text) ? text : `${text}Z`);
  return Number.isFinite(ms) && ms >= EARLIEST_VALID_MS ? ms : null;
}

/** Linear-interpolated quantile (R-7), matching the backend's quartiles. */
export function quantile(values: readonly number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const index = (sorted.length - 1) * p;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

function seatTypeOf(type: string | null | undefined): SeatType {
  return type?.toLowerCase().includes('wheelchair') ? 'wheelchair' : 'standard';
}

function paxBySeatType(passengers: ActivityTripRide['passengers']): SeatCounts {
  const counts = { standard: 0, wheelchair: 0 };
  for (const passenger of passengers ?? []) {
    counts[seatTypeOf(passenger.type)] += passenger.number_of_passengers ?? 0;
  }
  return counts;
}

function latLng(lat: number | undefined, lng: number | undefined): LatLngPair | null {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
}

export function rideTimings(activities: readonly SimulationActivity[]): RideTiming[] {
  const partial = new Map<string, PartialRide>();
  for (const activity of activities) {
    const shiftId = activity.vehicle_shift_id;
    if (!shiftId) continue;
    for (const trip of activity.trips ?? []) {
      if (trip.activity_type !== 'PICK' && trip.activity_type !== 'DROP') continue;
      const rideId = trip.rider_ride_id ?? trip.ride?.ride_id;
      const atMs = parseResultTime(trip.activity_start_time);
      if (!rideId || !trip.ride || atMs == null) continue;
      const entry = partial.get(rideId) ?? { shiftId, ride: trip.ride };
      if (trip.activity_type === 'PICK') entry.pickMs = atMs;
      else entry.dropMs = atMs;
      partial.set(rideId, entry);
    }
  }

  const rides: RideTiming[] = [];
  for (const [rideId, entry] of Array.from(partial)) {
    if (entry.pickMs == null || entry.dropMs == null) continue;
    rides.push({
      rideId,
      externalReferenceId: entry.ride.external_reference_id ?? null,
      shiftId: entry.shiftId,
      requestType: entry.ride.request_type ?? null,
      requestMs: parseResultTime(entry.ride.request_time),
      pickMs: entry.pickMs,
      dropMs: entry.dropMs,
      pax: paxBySeatType(entry.ride.passengers),
      from: latLng(entry.ride.from_latitude, entry.ride.from_longitude),
      to: latLng(entry.ride.to_latitude, entry.ride.to_longitude),
    });
  }
  return rides;
}

/** Arrival-fixed requests wait for their requested arrival, not their pickup; the backend counts early and late alike. */
export function waitMinutes(ride: RideTiming): number | null {
  if (ride.requestMs == null) return null;
  const gapMs = ride.requestType === 'arrival_fixed' ? ride.requestMs - ride.dropMs : ride.pickMs - ride.requestMs;
  return Math.abs(gapMs) / MINUTE_MS;
}

export function onBoardMinutes(ride: RideTiming): number {
  return (ride.dropMs - ride.pickMs) / MINUTE_MS;
}

export function serviceDay(vehicles: readonly ResultsVehicleRow[]): ServiceDay | null {
  let startMs = Infinity;
  let endMs = -Infinity;
  for (const row of vehicles) {
    const start = parseResultTime(row.start_time);
    const end = parseResultTime(row.end_time);
    if (start != null) startMs = Math.min(startMs, start);
    if (end != null) endMs = Math.max(endMs, end);
  }
  return Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs ? { startMs, endMs } : null;
}

function occupancySegments(
  startMs: number,
  endMs: number,
  stops: readonly StopEvent[],
  paxByRide: ReadonlyMap<string, SeatCounts>
): OccupancySegment[] {
  const aboard = { standard: 0, wheelchair: 0 };
  const segments: OccupancySegment[] = [];
  const pushWithinShift = (from: number, to: number) => {
    const clampedStart = Math.max(from, startMs);
    const clampedEnd = Math.min(to, endMs);
    if (clampedEnd > clampedStart) segments.push({ startMs: clampedStart, endMs: clampedEnd, aboard: { ...aboard } });
  };
  let cursor = startMs;
  for (const stop of stops) {
    pushWithinShift(cursor, stop.atMs);
    cursor = Math.max(cursor, stop.atMs);
    const pax = paxByRide.get(stop.rideId);
    const sign = stop.kind === 'pick' ? 1 : -1;
    aboard.standard += sign * (pax?.standard ?? 0);
    aboard.wheelchair += sign * (pax?.wheelchair ?? 0);
  }
  pushWithinShift(cursor, endMs);
  return segments;
}

function peakLoadFactor(segments: readonly OccupancySegment[], seats: SeatCounts): Record<SeatType, number | null> {
  const peak = (type: SeatType): number | null =>
    seats[type] > 0 ? segments.reduce((max, segment) => Math.max(max, segment.aboard[type]), 0) / seats[type] : null;
  return { standard: peak('standard'), wheelchair: peak('wheelchair') };
}

function stopEvents(rides: readonly RideTiming[]): StopEvent[] {
  return rides
    .flatMap((ride): StopEvent[] => [
      { atMs: ride.pickMs, kind: 'pick', rideId: ride.rideId },
      { atMs: ride.dropMs, kind: 'drop', rideId: ride.rideId },
    ])
    .sort((a, b) => a.atMs - b.atMs);
}

export function shiftSummaries(vehicles: readonly ResultsVehicleRow[], rides: readonly RideTiming[]): ShiftSummary[] {
  const shifts = new Map<string, { vehicleId: string | null; startMs: number; endMs: number; seats: Record<SeatType, number> }>();
  for (const row of vehicles) {
    const startMs = parseResultTime(row.start_time);
    const endMs = parseResultTime(row.end_time);
    if (!row.vehicle_shift_id || startMs == null || endMs == null) continue;
    const shift = shifts.get(row.vehicle_shift_id) ?? {
      vehicleId: row.vehicle_id ?? null,
      startMs,
      endMs,
      seats: { standard: 0, wheelchair: 0 },
    };
    shift.seats[seatTypeOf(row.capacity_type)] += row.seats ?? 0;
    shifts.set(row.vehicle_shift_id, shift);
  }

  const ridesByShift = new Map<string, RideTiming[]>();
  for (const ride of rides) {
    const list = ridesByShift.get(ride.shiftId) ?? [];
    list.push(ride);
    ridesByShift.set(ride.shiftId, list);
  }

  return Array.from(shifts.entries())
    .sort(([idA, a], [idB, b]) => a.startMs - b.startMs || idA.localeCompare(idB))
    .map(([shiftId, shift], index) => {
      const shiftRides = ridesByShift.get(shiftId) ?? [];
      const stops = stopEvents(shiftRides);
      const segments = occupancySegments(
        shift.startMs,
        shift.endMs,
        stops,
        new Map(shiftRides.map((ride) => [ride.rideId, ride.pax]))
      );
      return {
        shiftId,
        vehicleId: shift.vehicleId,
        ordinal: index + 1,
        startMs: shift.startMs,
        endMs: shift.endMs,
        seats: shift.seats,
        trips: shiftRides.length,
        pax: shiftRides.reduce((sum, ride) => sum + ride.pax.standard + ride.pax.wheelchair, 0),
        peakLoadFactor: peakLoadFactor(segments, shift.seats),
        segments,
        stops,
      };
    });
}

export function vehiclesUsed(shifts: readonly ShiftSummary[]): number {
  return shifts.filter((shift) => shift.trips > 0).length;
}

/** Each ride counted once; the range widens past the service day to keep requests made outside it. */
export function requestBuckets(
  rides: readonly RideTiming[],
  unmatched: readonly UnmatchedRide[],
  day: ServiceDay,
  bucketMinutes: number
): RequestBucket[] {
  const bucketMs = bucketMinutes * MINUTE_MS;
  const indexOf = (ms: number) => Math.floor((ms - day.startMs) / bucketMs);
  const matchedTimes = rides.flatMap((ride) => (ride.requestMs == null ? [] : [ride.requestMs]));
  const unmatchedTimes: number[] = [];
  const seenUnmatched = new Set<string>();
  for (const ride of unmatched) {
    const requestMs = parseResultTime(ride.request_time);
    const key = ride.ride_id ?? ride.external_reference_id;
    if (requestMs == null || (key != null && seenUnmatched.has(key))) continue;
    if (key != null) seenUnmatched.add(key);
    unmatchedTimes.push(requestMs);
  }
  const indexes = [...matchedTimes, ...unmatchedTimes].map(indexOf);
  const first = indexes.reduce((lowest, index) => Math.min(lowest, index), 0);
  const last = indexes.reduce((highest, index) => Math.max(highest, index), Math.ceil((day.endMs - day.startMs) / bucketMs) - 1);
  const buckets = Array.from({ length: last - first + 1 }, (_, i) => ({
    startMs: day.startMs + (first + i) * bucketMs,
    matched: 0,
    unmatched: 0,
  }));
  for (const ms of matchedTimes) buckets[indexOf(ms) - first].matched += 1;
  for (const ms of unmatchedTimes) buckets[indexOf(ms) - first].unmatched += 1;
  return buckets;
}

export function activeAt(
  atMs: number,
  rides: readonly RideTiming[],
  shifts: readonly ShiftSummary[]
): { tripsInProgress: number; vehiclesOut: number } {
  return {
    tripsInProgress: rides.filter((ride) => ride.pickMs <= atMs && atMs < ride.dropMs).length,
    vehiclesOut: shifts.filter((shift) => shift.startMs <= atMs && atMs <= shift.endMs).length,
  };
}

export function peakLoadByHour(shifts: readonly ShiftSummary[], day: ServiceDay): HourlyPeakLoad[] {
  return Array.from({ length: Math.ceil((day.endMs - day.startMs) / HOUR_MS) }, (_, i) => {
    const startMs = day.startMs + i * HOUR_MS;
    const endMs = startMs + HOUR_MS;
    const peak = (type: SeatType): number | null => {
      let max: number | null = null;
      for (const shift of shifts) {
        if (shift.seats[type] === 0) continue;
        for (const segment of shift.segments) {
          if (segment.endMs <= startMs || segment.startMs >= endMs) continue;
          max = Math.max(max ?? 0, segment.aboard[type] / shift.seats[type]);
        }
      }
      return max;
    };
    return { startMs, standard: peak('standard'), wheelchair: peak('wheelchair') };
  });
}

/** `kmByRide` holds each ride under its id and its external reference: look values up, never sum them. */
export function pathDistances(routes: Readonly<Record<string, RoadRouteWithMetadata>>): {
  kmByShift: ReadonlyMap<string, number>;
  kmByRide: ReadonlyMap<string, number>;
} {
  const kmByShift = new Map<string, number>();
  const kmByRide = new Map<string, number>();
  const seenLegs = new Set<string>();
  for (const route of Object.values(routes)) {
    const leg = route.metadata;
    if (!leg?.vehicle_shift_id || leg.segment_index == null || leg.distance_m == null) continue;
    // The paths service files a leg under every ride alias.
    const legKey = `${leg.vehicle_shift_id}:${leg.segment_index}`;
    if (seenLegs.has(legKey)) continue;
    seenLegs.add(legKey);
    const km = leg.distance_m / 1000;
    kmByShift.set(leg.vehicle_shift_id, (kmByShift.get(leg.vehicle_shift_id) ?? 0) + km);
    for (const rideId of leg.rides_onboard_ids ?? []) kmByRide.set(rideId, (kmByRide.get(rideId) ?? 0) + km);
  }
  return { kmByShift, kmByRide };
}

export function kpiDelta(
  value: number | null | undefined,
  compare: number | null | undefined,
  direction: KpiDirection
): { delta: number; tone: KpiTone } | null {
  if (value == null || compare == null) return null;
  const delta = value - compare;
  if (delta === 0 || direction === 'neutral') return { delta, tone: 'neutral' };
  const improved = direction === 'higher_is_better' ? delta > 0 : delta < 0;
  return { delta, tone: improved ? 'good' : 'bad' };
}
